import React, { useState, useCallback, useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Tooltip, Button } from 'framework/ui/mui-builder';
import { Modal } from 'framework/ui/ui-builder';
import FollowUpModal from '../../modals/FollowUpModal';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { FollowUpItem } from '../../types';

interface FollowUpSectionProps {
  followUps: FollowUpItem[];
  onRemove?: (id: string | number) => void;
  onUpdate?: (id: string | number, data: { description: string; date: string }) => void;
  maxItems?: number;
  title?: string;
}

const FollowUpSection: React.FC<FollowUpSectionProps> = ({ followUps, onRemove, onUpdate, maxItems = 5, title }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [followUpForUpdate, setFollowUpForUpdate] = useState<FollowUpItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [followUpToDelete, setFollowUpToDelete] = useState<FollowUpItem | null>(null);
  const { formatDate } = useDateService();

  const sortedFollowUps = useMemo(() => {
    return [...followUps].sort((a, b) => {
      const timeA = a.date ? new Date(a.date).getTime() : Infinity;
      const timeB = b.date ? new Date(b.date).getTime() : Infinity;
      if (Number.isNaN(timeA)) return 1;
      if (Number.isNaN(timeB)) return -1;
      return timeA - timeB;
    });
  }, [followUps]);

  const displayedFollowUps = sortedFollowUps.slice(0, maxItems);
  const hasMore = sortedFollowUps.length > maxItems;
  const remainingCount = sortedFollowUps.length - maxItems;

  const toggleModal = useCallback(() => {
    setModalOpen(prev => !prev);
  }, []);

  const handleFollowUpClick = useCallback((followUp: FollowUpItem) => {
    setFollowUpForUpdate(followUp);
    setModalOpen(true);
  }, []);

  const handleDeleteFollowUp = useCallback(
    async (followUp: FollowUpItem) => {
      if (!onRemove) return;

      setIsDeleting(true);
      try {
        await onRemove(followUp.id);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete follow-up:', err);
      } finally {
        setIsDeleting(false);
      }
    },
    [onRemove],
  );

  const handleFormSubmit = useCallback(
    async (requestData: any) => {
      if (followUpForUpdate && onUpdate) {
        return onUpdate(followUpForUpdate.id, {
          description: requestData.description,
          date: requestData.followUpDate,
        });
      }

      setModalOpen(false);
      return Promise.resolve();
    },
    [followUpForUpdate, onUpdate],
  );

  const closeModal = useCallback(() => {
    setFollowUpForUpdate(null);
    toggleModal();
  }, [toggleModal]);

  const openConfirmDelete = useCallback((followUp: FollowUpItem) => {
    setFollowUpToDelete(followUp);
    setConfirmDeleteOpen(true);
  }, []);

  const closeConfirmDelete = useCallback(() => {
    setConfirmDeleteOpen(false);
    setFollowUpToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!followUpToDelete) return;
    await handleDeleteFollowUp(followUpToDelete);
    closeConfirmDelete();
  }, [followUpToDelete, handleDeleteFollowUp, closeConfirmDelete]);

  return (
    <>
      <Box>
        {title && (
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle2" color="textSecondary">
              {title} ({followUps.length})
            </Typography>
          </Box>
        )}

        {displayedFollowUps.length === 0 ? (
          <Box display="flex" alignItems="center">
            <Typography variant="body1" color="textSecondary" fontStyle="italic">
              No follow-ups
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 150 }}>
            {displayedFollowUps.map(followUp => (
              <Box
                key={followUp.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Tooltip title={followUp.description} placement="top">
                  <Box>
                    <Button
                      link
                      onClick={() => handleFollowUpClick(followUp)}
                      sx={{ width: 110, justifyContent: 'flex-start' }}
                    >
                      <Typography variant="body2" color="primary">
                        {formatDate(followUp.date, DATE_FORMAT.NATURAL)}
                      </Typography>
                    </Button>
                  </Box>
                </Tooltip>
                <IconButton
                  sx={{ p: '1px', mt: 0.5 }}
                  onClick={() => openConfirmDelete(followUp)}
                  disabled={isDeleting || !onRemove}
                >
                  <CloseIcon sx={{ fontSize: '12px' }} color="error" />
                </IconButton>
              </Box>
            ))}
            {hasMore && (
              <Typography variant="body2" color="textSecondary">
                ...and {remainingCount} more
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <FollowUpModal
        isOpen={modalOpen}
        onClose={closeModal}
        followUpForUpdate={
          followUpForUpdate
            ? {
                ...followUpForUpdate,
                followUpDate:
                  typeof followUpForUpdate.date === 'string'
                    ? followUpForUpdate.date
                    : followUpForUpdate.date?.toString() || '',
                description: followUpForUpdate.description || '',
              }
            : undefined
        }
        submitFormFunc={async data => {
          await handleFormSubmit(data);
          return {
            id: followUpForUpdate?.id || Date.now(),
            description: (data.description as string) || '',
            followUpDate: (data.followUpDate as string) || '',
          };
        }}
      />

      <Modal
        isOpen={confirmDeleteOpen}
        title="Are you sure?"
        onToggle={closeConfirmDelete}
        buttons={[
          <Button
            key="confirm-delete"
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            Delete
          </Button>,
        ]}
      >
        Delete this follow-up?
      </Modal>
    </>
  );
};

export default FollowUpSection;
